/**
 * Helpers for PDF-import draft reports that lack a real S3 fileKey.
 * View/Download open the originally-imported PDF (scoped to WPS or PQR
 * pages) served by python-api.
 */

import { pythonApiFetchBlob } from '../mechanical-tests/api'
import { wpsImportBase } from '../wps-import/api/paths'

export const IMPORT_DRAFT_FILE_KEY = 'import-draft'

/** True when the buffer starts with the PDF magic bytes `%PDF`. */
export function isPdfMagic(buffer: ArrayBuffer): boolean {
  const head = new Uint8Array(buffer.slice(0, 4))
  return (
    head.length >= 4 &&
    head[0] === 0x25 && // %
    head[1] === 0x50 && // P
    head[2] === 0x44 && // D
    head[3] === 0x46 // F
  )
}

export function isUsableReportFileKey(fileKey: string | undefined | null): boolean {
  const key = typeof fileKey === 'string' ? fileKey.trim() : ''
  return key.length > 0 && key !== IMPORT_DRAFT_FILE_KEY
}

export function isImportDraftReport(report: {
  importDraft?: boolean
  fileKey?: string | null
}): boolean {
  if (report.importDraft === true) return true
  return !isUsableReportFileKey(report.fileKey)
}

export function isPdfImportPipeSet(meta: unknown): boolean {
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return false
  return (meta as { importSource?: unknown }).importSource === 'pdf'
}

/**
 * Fetch the originally-imported source PDF for an import-draft report and
 * return an object URL plus a friendly file name. `kind='wps'` returns the
 * WPS page(s); `kind='pqr'` returns the page(s) for `pqrNumber`.
 *
 * Callers OWN the returned `downloadUrl` and MUST call
 * `URL.revokeObjectURL(downloadUrl)` when done to avoid leaking blobs.
 */
export async function fetchImportSourcePdfUrl(params: {
  projectId: string
  pipeSetId: string
  extractionId: string
  kind: 'wps' | 'pqr'
  pqrNumber?: string
  fileName: string
}): Promise<{ downloadUrl: string; fileName: string }> {
  const query = new URLSearchParams({
    extraction_id: params.extractionId,
    kind: params.kind
  })
  if (params.kind === 'pqr' && params.pqrNumber) {
    query.set('pqr_number', params.pqrNumber)
  }
  const path = `${wpsImportBase(params.projectId, params.pipeSetId)}/source-pdf?${query.toString()}`
  const blob = await pythonApiFetchBlob(path, {
    headers: { Accept: 'application/pdf' }
  })
  const buffer = await blob.arrayBuffer()
  if (!isPdfMagic(buffer)) {
    throw new Error('Response is not a PDF')
  }
  const pdfBlob = new Blob([buffer], { type: 'application/pdf' })
  return {
    downloadUrl: URL.createObjectURL(pdfBlob),
    fileName: params.fileName
  }
}

export async function triggerBrowserDownload(
  fileUrl: string,
  fileName: string
): Promise<void> {
  try {
    const response = await fetch(fileUrl)
    const buffer = await response.arrayBuffer()
    if (!isPdfMagic(buffer)) {
      throw new Error('Response is not a PDF')
    }
    const blob = new Blob([buffer], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName || fileUrl.split('/').pop() || 'document.pdf'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    // Delay revoke so Chromium can finish reading the blob for download.
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
  } catch {
    window.open(fileUrl, '_blank', 'noopener,noreferrer')
  }
}
